export class ApiError extends Error {
  readonly code?: string;
  readonly requestId?: string;
  readonly httpStatus?: number;
  /**
   * The envelope's optional `details` object, verbatim.
   *
   * The §2.1 envelope is `{ error, code, details?, requestId }` and this was the
   * one field the client threw away, so anything the backend said about *which*
   * thing went wrong could only be recovered by reading the sentence in
   * `message` — the habit that made both offline-queue special cases dead code.
   * It is whatever the server sent, so read it defensively.
   */
  readonly details?: Record<string, unknown>;

  constructor(
    message: string,
    opts?: {
      code?: string;
      requestId?: string;
      httpStatus?: number;
      details?: Record<string, unknown>;
    }
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = opts?.code;
    this.requestId = opts?.requestId;
    this.httpStatus = opts?.httpStatus;
    this.details = opts?.details;
  }
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Builds the client-side error for a failed response out of the §2.1 envelope
 * `{ error, code, details?, requestId }`.
 *
 * Lifted out of `useLogisticsApi` for the same reason `classifyQueueFailure` was
 * lifted out of `useOfflineSync`: it is the part with the mapping in it, and the
 * hook around it cannot be reached by a test without an auth context and a fetch.
 * While it sat inline, `details` was silently dropped and nothing could notice —
 * and a dropped `details` does not fail, it quietly degrades every rule that reads
 * it back to the behaviour it was written to replace.
 *
 * Every field is checked rather than trusted. This is a server payload being turned
 * into something the queue will act on.
 */
export function apiErrorFromEnvelope(httpStatus: number, body: unknown): ApiError {
  const data = isPlainObject(body) ? body : {};
  const error = data.error;
  const message =
    typeof error === 'string' && error.length > 0 ? error : `Request failed: ${httpStatus}`;

  return new ApiError(message, {
    code: typeof data.code === 'string' ? data.code : undefined,
    requestId: typeof data.requestId === 'string' ? data.requestId : undefined,
    httpStatus,
    details: isPlainObject(data.details) ? data.details : undefined,
  });
}
